/**
 * Marketing landing — route: /
 *
 * Design source: Session E v2 — Marketing redesign (Marketing.html, 1191 lines).
 * Ported from the Claude Design output in:
 *   paul-handoff/design-iterations/claude-design-output-marketing/elreign/project/Marketing.html
 *
 * Brand vocabulary:
 *   - Hero "library room": deep teal gradient (#0E2A26 → #1F4D45) with brass radial bleed.
 *   - Below-hero surface: light cream (#FAF7F0 / #F8F5EE) per UX spec (older user base,
 *     professional fiduciary feel). Dark theme applies to pitch decks only.
 *   - Brass accent: --accent-400 (#C19A5B) primary CTA on dark; --accent-600 on light.
 *
 * Animation behaviour:
 *   - Hero chart: SVG stroke-dashoffset draw-on via `marketing-drawLine` keyframe.
 *   - Chain joints: staggered `marketing-jointPulse` keyframe (4.5s, 0.6s stagger).
 *   - Traveling signal: `marketing-lineTravel` keyframe along brass line.
 *   - Pulse ring on macframe: `marketing-pulseRing`.
 *   - All animations respect `prefers-reduced-motion: reduce` (paused in design-system.css).
 *
 * All keyframes live in frontend/src/styles/design-system.css under the
 * "Marketing surface — Landing (Session E v2)" section, prefixed `marketing-`
 * to avoid collision with any existing keyframe names.
 *
 * Route links:
 *   - All internal routes use TanStack <Link to="...">.
 *   - "Sign in" CTAs → <Link to="/auth/login"> (SWA serves both on the same domain).
 */

import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { ReignMark } from '../components/reign-mark';
import { useSession } from '../lib/use-session';
import { preOnboardingRouteForSession } from '../lib/auth-landing';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

function LandingPage() {
  // Safety-net for the email-confirm dead-end (2026-07-03, QA): a signed-in but
  // PRE-ONBOARDING user (no app_metadata.tenant_id) can land here on '/' — the
  // marketing root — if anything routes them to the apex instead of onboarding.
  // The CONFIG root cause (Supabase's Redirect-URL allowlist falling back to the
  // Site URL on a miss) is fixed live, but this recovers deterministically
  // regardless of future config drift: bounce a confirmed, tenant-less session
  // straight to its onboarding surface rather than stranding them on a signed-in
  // marketing page with no onward path. An ONBOARDED user (tenant_id present) and
  // an anonymous visitor both keep seeing marketing untouched. Gate only once the
  // session has hydrated (loading), so we never flash-redirect before it is known.
  const { session, loading } = useSession();
  if (!loading && session && !session.user?.app_metadata?.tenant_id) {
    return <Navigate to={preOnboardingRouteForSession(session)} replace />;
  }

  return (
    <div className="marketing-page">

      {/* ============================================================ HEADER */}
      <header className="site">
        <div className="shell header-row">
          <Link className="brand-row" to="/" aria-label="Elreign home">
            <ReignMark size={30} />
            <span className="wordmark">elreign</span>
          </Link>
          <nav className="primary">
            <Link to="/product" className="active">Product</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/security">Security</Link>
            <Link to="/customers">Customers</Link>
            <Link to="/about">About</Link>
          </nav>
          {/* FE-10 (Henry R2-012): the marketing nav previously offered only
              "Sign in" + a firm-pricing CTA, with no entry point for a direct
              individual to create their own account. Add a "Create account"
              link → /auth/signup (the audience-neutral self-service path) so
              individuals aren't funnelled exclusively through the firm CTA. */}
          <div className="header-right">
            <Link className="signin-link" to="/auth/login">Sign in</Link>
            <Link className="signin-link" to="/auth/signup">Create account</Link>
            <Link className="btn btn-on-dark" to="/firms">See pricing for firms</Link>
          </div>
        </div>
      </header>

      {/* ============================================================ HERO */}
      <section className="hero">
        <div className="shell hero-grid">

          <div className="hero-left">
            <div className="hero-eyebrow">
              <span className="seal"></span>
              <span className="eyebrow on-dark">Trust administration · Built for South Africa</span>
            </div>
            <h1 className="hero-title">Trust administration <em>that holds in court.</em></h1>
            <p className="hero-sub">Trust administration software for South African fiduciary firms, trustees and directors, and the families they serve.</p>
            {/* FE-10: two clear front doors — fiduciary firms to the firm
                pricing page, and direct individuals to self-service signup. */}
            <div className="hero-ctas">
              <Link className="btn btn-brass btn-lg" to="/firms">See pricing for firms</Link>
              <Link className="btn btn-on-dark btn-lg" to="/auth/signup">Create your account</Link>
            </div>
            <div className="hero-proof-strip" aria-hidden="true">
              <span>For trust administrators, trustees and directors</span>
              <span className="dot">·</span>
              <span>Built for POPIA &amp; FICA</span>
              <span className="dot">·</span>
              <span>Guided onboarding on your real book</span>
            </div>
          </div>

          {/* ====== Hero product shot — generic trustee dashboard mockup ====== */}
          <div className="hero-right">
            <div className="macframe">
              <div className="macframe-bar">
                <div className="traffic"><span className="r"></span><span className="y"></span><span className="g"></span></div>
                <div className="macframe-url">
                  <div className="macframe-url-pill"><span className="lock" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>app.elreign.io / overview</div>
                </div>
                <div style={{ width: '54px' }}></div>
              </div>
              <div className="macframe-pulse">
                <span className="pulse-dot"></span>
                Updated 2m ago · chain verified
              </div>

              <div className="dash">
                {/* nav */}
                <aside className="dash-nav">
                  <div className="dash-brand">
                    <svg className="reign-mark" viewBox="0 0 120 120" fill="none" aria-hidden="true">
                      <rect x="20" y="14" width="14" height="92" rx="0.5" fill="#143832"/>
                      <rect x="20" y="14" width="64" height="14" rx="0.5" fill="#143832"/>
                      <rect x="20" y="53" width="42" height="14" rx="0.5" fill="#143832"/>
                      <rect x="20" y="92" width="58" height="14" rx="0.5" fill="#143832"/>
                      <circle cx="86" cy="79" r="6" fill="#C19A5B"/>
                    </svg>
                    <span className="wm">elreign</span>
                  </div>
                  <div>
                    <div className="dash-nav-label">M.L.</div>
                    <div className="dash-nav-list">
                      <div className="dash-nav-item active">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>
                        Overview
                      </div>
                      <div className="dash-nav-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h16M4 17h10"/></svg>
                        Trusts <span className="dash-nav-badge">4</span>
                      </div>
                      <div className="dash-nav-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M8 4h8l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><circle cx="12" cy="14" r="2.4"/></svg>
                        Resolutions <span className="dash-nav-badge" style={{ background: 'var(--accent-400)', color: 'var(--primary-900)' }}>2</span>
                      </div>
                      <div className="dash-nav-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M9 9h6M9 13h6M9 17h4"/></svg>
                        Documents
                      </div>
                      <div className="dash-nav-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="9" r="3"/><circle cx="17" cy="11" r="2.4"/><path d="M3 19c0-2.5 2.6-4 6-4s6 1.5 6 4"/><path d="M14 19c0-1.7 1.5-3 3.4-3 1.6 0 3.6 1 3.6 3"/></svg>
                        Beneficiaries
                      </div>
                      <div className="dash-nav-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>
                        Tax
                      </div>
                      <div className="dash-nav-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12c0 4-4 8-9 8a10 10 0 0 1-4-1L3 20l1-4a8 8 0 0 1-1-4c0-4 4-8 9-8s9 4 9 8z"/></svg>
                        Messages
                      </div>
                    </div>
                  </div>
                </aside>

                {/* main */}
                <div className="dash-main">
                  <div className="dash-greeting">
                    <div>
                      <div className="eyebrow"><span className="live-dot"></span>Welcome back</div>
                      <h2>4 trusts <em>in good standing</em></h2>
                      <div className="sub">Last reviewed <b>30 March 2026</b> · Next FICA review <b>31 March 2027</b></div>
                    </div>
                    <div className="icon-btns">
                      <span className="ib"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg></span>
                      <span className="ib"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9z"/><path d="M10 19a2 2 0 0 0 4 0"/></svg></span>
                    </div>
                  </div>

                  <div className="dash-hero">
                    <div className="dh-left">
                      <div className="dh-tag">
                        <span className="seal">f</span>
                        <span className="name">Family Property Trust</span>
                      </div>
                      <div className="dh-label">Total assets under administration</div>
                      <div className="dh-value num"><span className="currency">R</span>3,427,510<span className="cents">.40</span></div>
                      <div className="dh-deltas">
                        <span className="dh-pill">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M9 7h8v8"/></svg>
                          +5.2%
                        </span>
                      </div>
                      <div className="dh-tabs">
                        <span className="tab">3M</span><span className="tab active">12M</span><span className="tab">All</span>
                      </div>
                    </div>
                    {/* Chart */}
                    <div className="dh-chart" aria-hidden="true">
                      <svg viewBox="0 0 460 140" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor="#1F4D45" stopOpacity="0.22"/>
                            <stop offset="100%" stopColor="#1F4D45" stopOpacity="0"/>
                          </linearGradient>
                        </defs>
                        {/* gridlines */}
                        <g stroke="rgba(20,18,12,0.06)" strokeWidth="1">
                          <line x1="0" y1="30" x2="460" y2="30"/>
                          <line x1="0" y1="70" x2="460" y2="70"/>
                          <line x1="0" y1="110" x2="460" y2="110"/>
                        </g>
                        {/* benchmark dashed */}
                        <path d="M0 100 C 60 96, 120 92, 180 88 S 300 78, 360 70 S 440 58, 460 50" fill="none" stroke="#A37E42" strokeWidth="1.3" strokeDasharray="3 3" opacity="0.6"/>
                        {/* area */}
                        <path d="M0 110 L 30 102 L 60 95 L 95 100 L 130 86 L 165 80 L 200 72 L 235 62 L 270 64 L 305 50 L 340 44 L 380 38 L 420 32 L 460 26 L 460 140 L 0 140 Z" fill="url(#g1)"/>
                        {/* line */}
                        <path className="draw-line" d="M0 110 L 30 102 L 60 95 L 95 100 L 130 86 L 165 80 L 200 72 L 235 62 L 270 64 L 305 50 L 340 44 L 380 38 L 420 32 L 460 26" fill="none" stroke="#1F4D45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        {/* end dot */}
                        <circle className="end-dot" cx="460" cy="26" r="3.5" fill="#1F4D45"/>
                        <circle className="end-dot" cx="460" cy="26" r="6.5" fill="none" stroke="#1F4D45" strokeOpacity="0.2" strokeWidth="2"/>
                      </svg>
                    </div>
                  </div>

                  {/* second row */}
                  <div className="dash-row">
                    <div className="dash-card">
                      <div className="dash-card-head">
                        <h3 className="dash-card-title">Allocation</h3>
                        <span className="dash-card-meta">4 holdings</span>
                      </div>
                      <div className="alloc-grid-mini">
                        <div className="donut-mini">
                          <div className="donut-mini-center">
                            <div className="l1">Mix</div>
                            <div className="l2">Property-led</div>
                          </div>
                        </div>
                        <div className="alloc-mini-list">
                          <div className="alloc-mini-row"><span className="sw" style={{ background: 'var(--primary-700)' }}></span><span>Property</span><span className="pct">41%</span></div>
                          <div className="alloc-mini-row"><span className="sw" style={{ background: 'var(--accent-500)' }}></span><span>Equities</span><span className="pct">26%</span></div>
                          <div className="alloc-mini-row"><span className="sw" style={{ background: 'var(--primary-400)' }}></span><span>Cash</span><span className="pct">17%</span></div>
                          <div className="alloc-mini-row"><span className="sw" style={{ background: 'var(--accent-300)' }}></span><span>Bonds</span><span className="pct">16%</span></div>
                        </div>
                      </div>
                    </div>

                    <div className="dash-card">
                      <div className="dash-card-head">
                        <h3 className="dash-card-title">Holdings</h3>
                        <span className="dash-card-meta">12m</span>
                      </div>
                      <div className="holdings-mini">
                        <div className="holdings-mini-row">
                          <div className="nm">Bellville Unit 101<small>Property</small></div>
                          <div className="amt num">R 1,425,000</div>
                          <div className="pp pos">+4.1%</div>
                        </div>
                        <div className="holdings-mini-row">
                          <div className="nm">Allan Gray Equity<small>Equities</small></div>
                          <div className="amt num">R 882,400</div>
                          <div className="pp pos">+8.3%</div>
                        </div>
                        <div className="holdings-mini-row">
                          <div className="nm">Money market<small>Cash</small></div>
                          <div className="amt num">R 580,110</div>
                          <div className="pp pos">+1.2%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============================================================ MOBILE SECTION */}
      <section className="section-mobile">
        <div className="shell mobile-grid">

          <div>
            <div className="hero-eyebrow">
              <span className="seal" style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-400)' }}></span>
              <span className="eyebrow">For the families they serve</span>
            </div>
            <h2 className="section-h">Sign with <em>confidence.</em></h2>
            <p className="section-sub">A signing request arrives as a resolution. You compare the fingerprint sent over SMS against the one on screen. You sign with Face ID or fingerprint. The chain seals the signature against the resolution body, the device, and the timestamp.</p>

            <div className="stamp-list">
              <div className="stamp-item">
                <span className="stamp-mark">i</span>
                <p className="stamp-text">Distribution requests arrive as <b>resolutions</b>, not emails.</p>
              </div>
              <div className="stamp-item">
                <span className="stamp-mark">ii</span>
                <p className="stamp-text">Every signature is checked against a <b>fingerprint sent out of band</b>.</p>
              </div>
              <div className="stamp-item">
                <span className="stamp-mark">iii</span>
                <p className="stamp-text">Sealed on the <b>audit chain</b> at the moment of signature.</p>
              </div>
            </div>
            {/* Beno feedback (2026-07-15): `.mobile-cta-card` only carried
                rules inside the <900px media query, so on desktop this line
                rendered as plain unstyled text sitting flush under the
                stamp-list — no breathing room, no italic treatment. Inline
                styles here make it consistent at every width without
                touching the shared stylesheet. */}
            <Link
              className="mobile-cta-card"
              to="/auth/signup"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                marginTop: 36,
                textDecoration: 'none',
              }}
            >
              <span
                className="cta-label"
                style={{
                  fontFamily: 'var(--serif)',
                  fontStyle: 'italic',
                  fontSize: 17,
                  color: 'var(--ink)',
                  letterSpacing: '-0.005em',
                  lineHeight: 1.35,
                }}
              >
                Sign in to receive a resolution on your phone
              </span>
              <span
                className="cta-arrow"
                aria-hidden="true"
                style={{ color: 'var(--accent-600)', fontSize: 20, flexShrink: 0 }}
              >
                →
              </span>
            </Link>
          </div>

          <div className="phone-stage">
            <div className="phone">
              <div className="phone-screen">
                <div className="phone-statusbar">
                  <span className="num">9:41</span>
                  <span className="ind">
                    <svg viewBox="0 0 18 12" fill="currentColor" aria-hidden="true"><path d="M1 8h2v3H1zM5 6h2v5H5zM9 4h2v7H9zM13 2h2v9h-2z"/></svg>
                    <svg viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true"><rect x="1" y="2" width="18" height="8" rx="2"/><rect x="3" y="4" width="14" height="4" rx="1" fill="currentColor"/><path d="M21 5v2"/></svg>
                  </span>
                </div>
                <div className="phone-notch"></div>

                <div className="demo-slot">
                  <div className="demo-slot-tag"><span className="rec"></span>VIDEO LOOP · DROP IN</div>
                  <div className="demo-slot-title">A trustee signing a resolution.</div>

                  <div className="demo-slot-storyboard">
                    <div className="sb"><span className="n">01</span><span>Dashboard</span><span className="t">3s</span></div>
                    <div className="sb"><span className="n">02</span><span>Tap &ldquo;Review &amp; sign&rdquo;</span><span className="t">1s</span></div>
                    <div className="sb"><span className="n">03</span><span>Resolution sheet</span><span className="t">2s</span></div>
                    <div className="sb"><span className="n">04</span><span>Fingerprint match</span><span className="t">1s</span></div>
                    <div className="sb"><span className="n">05</span><span>Toggle, toggle, toggle</span><span className="t">2s</span></div>
                    <div className="sb"><span className="n">06</span><span>Face ID</span><span className="t">2s</span></div>
                    <div className="sb"><span className="n">07</span><span>Wax seal</span><span className="t">3s</span></div>
                  </div>

                  <div className="demo-slot-meta">14s loop · captured from app.elreign.io/m</div>
                </div>

                <div className="play-pill">
                  <span className="triangle"></span>
                  <span>autoplay · muted · loop</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============================================================ ADMIN SECTION */}
      <section className="section-admin">

        {/* Full-bleed statement band — Beno feedback (2026-07-15): the
            "Built for fiduciary firms" intro read as a small eyebrow buried
            next to the demo card; it needed to land as its own statement
            above the section's demo content. Full-bleed via the classic
            100vw + negative-margin trick so it escapes the .shell max-width
            even though it lives inside one. Colours are literal hex per the
            brief — no matching token exists in design-system.css for this
            deep-green/gold combination (that file is out of scope for this
            pass; everything here is inline). */}
        <div
          style={{
            width: '100vw',
            marginLeft: 'calc(50% - 50vw)',
            // Paul (2026-07-15): the demo card sat cramped right under the
            // band — give the statement room to land before the demo starts.
            marginBottom: '72px',
            background: '#14281E',
            backgroundImage:
              'radial-gradient(ellipse at 90% -20%, rgba(201,169,97,.14), transparent 55%)',
            padding: '84px 32px',
          }}
        >
          <div className="shell" style={{ padding: 0 }}>
            <h2
              style={{
                fontFamily: 'var(--serif)',
                fontWeight: 300,
                fontSize: '58px',
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                margin: 0,
                color: 'var(--bg)',
              }}
            >
              Built{' '}
              <em style={{ fontStyle: 'italic', fontWeight: 400, color: '#C9A961' }}>
                for fiduciary firms.
              </em>
            </h2>
            <div style={{ width: '64px', height: '1px', background: '#C9A961', opacity: 0.6, margin: '28px 0' }} />
            <p style={{ fontSize: '19px', lineHeight: 1.5, color: '#C9C4AE', maxWidth: '620px', margin: 0 }}>
              FICA pipelines, lodgement queues, and the audit chain in one place. White-label your tenant subdomain. Charge by the user, not the trust.
            </p>
          </div>
        </div>

        <div className="shell admin-grid">

          {/* left: browser frame */}
          <div>
            <div className="browser">
              <div className="browser-bar">
                <div className="browser-actions">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12a8 8 0 0 1 14-5l2 2"/><path d="M20 4v5h-5"/></svg>
                </div>
                <div className="browser-tabs">
                  <div className="browser-tab active">
                    <span className="fav">e</span>
                    <span>Admin · firm overview</span>
                    <span className="x">×</span>
                  </div>
                  <div className="browser-tab">
                    <span className="fav" style={{ background: 'var(--accent-400)', color: 'var(--primary-800)' }}>f</span>
                    <span>Family Property Trust</span>
                  </div>
                  <div className="browser-tab">
                    <span className="fav" style={{ background: 'var(--ink)', color: 'var(--accent-300)' }}>c</span>
                    <span>Coastal Holdings</span>
                  </div>
                </div>
                <div className="traffic" style={{ marginLeft: 'auto' }}><span className="r"></span><span className="y"></span><span className="g"></span></div>
              </div>
              <div className="browser-url">
                <div className="browser-url-pill"><span className="lock" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>app.elreign.io / <span className="fade">admin / pipeline / lodgements</span></div>
                <div className="browser-actions">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="6" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="12" cy="18" r="1.4"/></svg>
                </div>
              </div>

              <div className="browser-content">
                <div className="demo-slot" style={{ padding: '60px 40px' }}>
                  <div className="demo-slot-tag"><span className="rec"></span>VIDEO LOOP · DROP IN</div>
                  <div className="demo-slot-title" style={{ fontSize: '28px', maxWidth: '520px' }}>A senior trust administrator running 47 trusts before lunch.</div>

                  <div className="demo-slot-storyboard" style={{ maxWidth: '480px', flexDirection: 'row', flexWrap: 'wrap', gap: '6px' }}>
                    <div className="sb" style={{ flex: '1 0 220px' }}><span className="n">01</span><span>Dashboard · KPIs &amp; FICA</span><span className="t">3s</span></div>
                    <div className="sb" style={{ flex: '1 0 220px' }}><span className="n">02</span><span>Open ICMS pipeline</span><span className="t">2s</span></div>
                    <div className="sb" style={{ flex: '1 0 220px' }}><span className="n">03</span><span>Drag &rarr; Awaiting submission</span><span className="t">2s</span></div>
                    <div className="sb" style={{ flex: '1 0 220px' }}><span className="n">04</span><span>Branding Studio</span><span className="t">2s</span></div>
                    <div className="sb" style={{ flex: '1 0 220px' }}><span className="n">05</span><span>Re-skin tenant live</span><span className="t">2s</span></div>
                    <div className="sb" style={{ flex: '1 0 220px' }}><span className="n">06</span><span>Back to dashboard</span><span className="t">1s</span></div>
                  </div>

                  <div className="demo-slot-meta">12s loop · captured from app.elreign.io/admin</div>
                </div>
                <div className="play-pill">
                  <span className="triangle"></span>
                  <span>autoplay · muted · loop</span>
                </div>
              </div>
            </div>
          </div>

          {/* right: copy */}
          <div>
            {/* Headline + sub now live in the full-bleed band above; this
                column keeps the two supporting lists unchanged in content. */}
            <div className="stamp-list">
              <div className="stamp-item">
                <span className="stamp-mark">i</span>
                <p className="stamp-text"><b>Master ICMS lodgements</b> queued and tracked end to end.</p>
              </div>
              <div className="stamp-item">
                <span className="stamp-mark">ii</span>
                <p className="stamp-text">White-label subdomain at <b style={{ fontFamily: 'var(--mono)', fontSize: '14px' }}>firm.elreign.io</b></p>
              </div>
              <div className="stamp-item">
                <span className="stamp-mark">iii</span>
                <p className="stamp-text">Audit chain holds in court: every <b>signature</b>, every <b>BO update</b>.</p>
              </div>
            </div>

            {/* Beno feedback (2026-07-15): "Admin highlights" (.admin-feature-
                stack / .afs-card) only carried rules inside the <900px media
                query — on desktop these three lines rendered with no card
                styling at all, i.e. plain unstyled divs at inconsistent
                inherited sizes (his "mixed fonts, cramped lines" note). Rather
                than lean on that mobile-only CSS, this reuses the page's own
                proven stamp-list pattern (same one used twice elsewhere on
                this page) so the three benefits render as a single-font-size,
                evenly-spaced bulleted list at every width, and gets an actual
                heading instead of just an aria-label. */}
            <h3
              id="admin-benefits-heading"
              className="serif"
              style={{
                fontSize: '20px',
                fontStyle: 'italic',
                fontWeight: 300,
                letterSpacing: '-0.01em',
                color: 'var(--ink)',
                margin: '32px 0 16px',
              }}
            >
              Benefits.
            </h3>
            <div className="stamp-list" aria-labelledby="admin-benefits-heading">
              <div className="stamp-item">
                <span className="stamp-mark">i</span>
                <p className="stamp-text"><b>FICA pipeline</b>: 8 trusts due in 30 days. Lodge with the Master in two taps.</p>
              </div>
              <div className="stamp-item">
                <span className="stamp-mark">ii</span>
                <p className="stamp-text"><b>Branding Studio</b>: <span style={{ fontFamily: 'var(--mono)', fontSize: '14px' }}>firm.elreign.io</span> with your logo. Live preview as you change colour and tone.</p>
              </div>
              <div className="stamp-item">
                <span className="stamp-mark">iii</span>
                <p className="stamp-text"><b>Audit explorer</b>: trace one hash across every tenant. Every signature, every BO update, in one chain.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============================================================ CHAIN */}
      <section className="section-chain">
        <div className="shell">

          <div className="chain-head">
            <span className="eyebrow">The audit chain</span>
            <h2>Every event. Forever. <em>Linked.</em></h2>
          </div>

          <div className="chain-track">
            <div className="chain-line">
              <div className="signal" aria-hidden="true"></div>
            </div>

            <div className="chain-cards">

              {/* Chain cards intentionally show anonymised initials + role
                  only — no real-looking PII on the public landing page.
                  The visual demonstrates the audit-chain pattern; actual
                  trustee names only ever appear inside the authenticated
                  product surface for the trust's authorised users. */}
              <article className="chain-card">
                <span className="chain-joint"></span>
                <div className="chain-date">2026-04-12 · 09:14 SAST</div>
                <div className="chain-event">Resolution drafted</div>
                <div className="chain-actor">
                  <div className="av teal-soft">PJ</div>
                  <div className="chain-actor-meta">
                    <span className="chain-actor-name">P.J.</span>
                    <span className="chain-actor-role">Fiduciary admin</span>
                  </div>
                </div>
                <div className="chain-hash"><span className="prev">a3f1&hellip;</span> &rarr; 7c4e2b&hellip;</div>
              </article>

              <article className="chain-card">
                <span className="chain-joint"></span>
                <div className="chain-date">2026-04-13 · 11:02 SAST</div>
                <div className="chain-event">Fingerprint sent</div>
                <div className="chain-actor">
                  <div className="av ink">SY</div>
                  <div className="chain-actor-meta">
                    <span className="chain-actor-name">System</span>
                    <span className="chain-actor-role">Out-of-band SMS</span>
                  </div>
                </div>
                <div className="chain-hash"><span className="prev">7c4e&hellip;</span> &rarr; 2bd819&hellip;</div>
              </article>

              <article className="chain-card featured">
                <span className="chain-joint"></span>
                <div className="chain-date">2026-04-14 · 14:32 SAST</div>
                <div className="chain-event">Resolution signed</div>
                <div className="chain-actor">
                  <div className="av brass">A.V.</div>
                  <div className="chain-actor-meta">
                    <span className="chain-actor-name">A.V.</span>
                    <span className="chain-actor-role">Trustee &middot; Face ID</span>
                  </div>
                </div>
                <div className="chain-hash"><span className="prev">2bd8&hellip;</span> &rarr; c9af47&hellip;</div>
              </article>

              {/* Beno feedback (2026-07-15): "BO register updated" read as a
                  step OF the resolution-signing flow, which is wrong — the
                  BO register is a separate record, not part of a resolution's
                  audit trail. Removed rather than retitled, so lodgement now
                  reads correctly as the very next step after signing (the
                  domain truth: signatures first, then — for the documents
                  that require it — lodgement with the Master). The hash
                  chain below is relinked to point at the signing card's
                  output instead of the removed card's. */}
              <article className="chain-card">
                <span className="chain-joint"></span>
                <div className="chain-date">2026-04-15 · 10:08 SAST</div>
                <div className="chain-event">Lodged with the Master</div>
                <div className="chain-actor">
                  <div className="av teal-soft">EL</div>
                  <div className="chain-actor-meta">
                    <span className="chain-actor-name">Elreign Legacy (Pty) Ltd</span>
                    <span className="chain-actor-role">Receipt 2026/4815 &middot; where required</span>
                  </div>
                </div>
                <div className="chain-hash"><span className="prev">c9af&hellip;</span> &rarr; 8d6f1e&hellip;</div>
              </article>

            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <span className="chain-receipt"><span className="pip"></span>Cryptographic audit chain · Signed receipts, our key on request · FICA s23 retention</span>
          </div>

        </div>
      </section>

      {/* ============================================================ COMPLIANCE */}
      <section className="section-compliance">
        <div className="shell">
          <div className="comp-head">
            <span className="eyebrow">Compliance posture</span>
            <h2>Three commitments <em>in writing.</em></h2>
          </div>

          <div className="comp-grid">
            <article className="comp-card">
              <span className="stamp"><span className="seal">P</span><span className="label">POPIA · BY DESIGN</span></span>
              <h3>Hosted in South Africa. Always.</h3>
              <p>Microsoft Azure South Africa North (Johannesburg primary), with geo-redundant fallback to Azure South Africa West (Cape Town). Your data never leaves the country. Region: <code>southafricanorth</code>.</p>
            </article>

            <article className="comp-card">
              <span className="stamp"><span className="seal">F</span><span className="label">FICA s23 · RETENTION</span></span>
              <h3>Five years. Worm-locked.</h3>
              <p>Signed receipts, with a manifest giving each bundle&rsquo;s SHA-256 and the id of the key that signed it. Every signature, every BO update, every lodgement: on the record and recoverable on demand.</p>
            </article>

            <article className="comp-card">
              <span className="stamp"><span className="seal">S</span><span className="label">SUBPROCESSORS</span></span>
              <h3>Listed publicly. No surprises.</h3>
              <p>Listed at <code>/legal/subprocessors</code>, updated in writing <b>30 days</b> before any change. No silent additions.</p>
            </article>
          </div>

          <p className="comp-foot">Read the full security disclosure <Link to="/legal/security">at /legal/security &rarr;</Link></p>
        </div>
      </section>

      {/* pricing teaser removed — pricing lives on /pricing for two audiences */}

      {/* ============================================================ FAQ */}
      <section className="section-faq">
        <div className="shell">
          <div className="faq-head">
            <span className="eyebrow">Questions, answered plainly</span>
            <h2>Things firms ask <em>before they sign.</em></h2>
          </div>

          <div className="faq-list">

            <details className="faq-item" open>
              <summary className="faq-summary">
                <span className="faq-num">01</span>
                <span className="faq-q">Why not just use spreadsheets?</span>
                <span className="faq-toggle"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg></span>
              </summary>
              <div className="faq-answer">
                <p>Regulators don&rsquo;t accept spreadsheets as audit trail anymore. Beneficiaries don&rsquo;t either. The chain has to be cryptographic, the retention has to be FICA s23 compliant, and a row in a workbook with someone&rsquo;s initials is none of those things.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                <span className="faq-num">02</span>
                <span className="faq-q">How do you handle POPIA?</span>
                <span className="faq-toggle"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg></span>
              </summary>
              <div className="faq-answer">
                <p>Our platform is architected for POPIA: client and trust records live in <span className="mono">southafricanorth</span> and stay in South Africa, audit entries are pseudonymised, and erasure honours FICA retention windows. Formal compliance attestation is part of our pre-launch counsel review.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                <span className="faq-num">03</span>
                <span className="faq-q">Can my fiduciary firm white-label this?</span>
                <span className="faq-toggle"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg></span>
              </summary>
              <div className="faq-answer">
                <p>Yes. Practice and Enterprise tiers include a tenant subdomain at <span className="mono">firm.elreign.io</span> with your firm&rsquo;s logo, palette, and signatory name on every email and resolution. Set it up in the Branding Studio in about an hour.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                <span className="faq-num">04</span>
                <span className="faq-q">What if Elreign goes away?</span>
                <span className="faq-toggle"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg></span>
              </summary>
              <div className="faq-answer">
                {/* MKT-01 (ruling 8, 2026-09-03 "soften now"; review 2 of the
                    rulings wave, finding D2). This promised a "public
                    verifier". There is not one: no published key, no tool to
                    download, no written hash format. What exists is the signed
                    export and its manifest, which is what product.tsx now says.
                    The copy lints check punctuation, not truth, so this line
                    survived the softening pass on product.tsx. */}
                <p>The audit chain is portable. Every export is a signed bundle with a manifest giving its SHA-256 and the id of the key that signed it, so anyone holding that key can recheck the chain on their own machine. We do not publish the key or a verifier tool yet; ask us and both are yours. Your trusts don&rsquo;t depend on us being here, now and in the future.</p>
              </div>
            </details>

          </div>
        </div>
      </section>

      {/* ============================================================ CLOSER */}
      <section className="section-closer">
        <div className="shell">
          <div className="closer-card">
            <span className="monogram" aria-hidden="true">e</span>
            <div>
              <span className="eyebrow on-dark">Begin</span>
              <h2>Walk through <em>your first trust with us.</em></h2>
              <p>Thirty-day trial on every tier. No card to start. We&rsquo;ll walk through your first trust together.</p>
              {/* No phone number: "+27 82 555 0101" was invented (Paul, 22 Sep
                  2026), and a number that rings nobody is worse than none. */}
              <div className="closer-mono">paul@elreign.io · Stellenbosch, 7600</div>
            </div>
            <div className="closer-actions">
              <Link className="btn btn-brass btn-lg" to="/contact">Talk to us</Link>
              <Link className="btn btn-ghost btn-lg" to="/auth/login">Sign in</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ FOOTER */}
      <footer className="site">
        <div className="shell">
          <div className="footer-grid">
            <div className="footer-brand">
              <svg className="reign-mark" viewBox="0 0 120 120" fill="none" aria-hidden="true">
                <rect x="20" y="14" width="14" height="92" rx="0.5" fill="#143832"/>
                <rect x="20" y="14" width="64" height="14" rx="0.5" fill="#143832"/>
                <rect x="20" y="53" width="42" height="14" rx="0.5" fill="#143832"/>
                <rect x="20" y="92" width="58" height="14" rx="0.5" fill="#143832"/>
                <circle cx="86" cy="79" r="6" fill="#C19A5B"/>
              </svg>
              <p className="footer-tag">Quiet rigour for fiduciary work.</p>
            </div>

            <div className="footer-col">
              <h4>Product</h4>
              <ul>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/security">Security</Link></li>
                <li><Link to="/status">Status</Link></li>
                <li><Link to="/legal/subprocessors">Sub-processors</Link></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><Link to="/help">Help</Link></li>
                <li><Link to="/legal/trust-centre">Trust Centre</Link></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/legal/privacy">Privacy</Link></li>
                <li><Link to="/legal/paia">PAIA</Link></li>
                <li><Link to="/legal/terms">Terms</Link></li>
                <li><Link to="/legal/cookies">Cookies</Link></li>
              </ul>
            </div>
          </div>

          <div className="footer-foot">
            <span>&copy; Elreign Legacy (Pty) Ltd · Cape Town</span>
            <span className="region">Hosted in Microsoft Azure South Africa North</span>
            <div className="legal-links">
              <Link to="/legal/privacy">Privacy</Link>
              <Link to="/legal/terms">Terms</Link>
              <Link to="/legal/cookies">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
